import {
  collection,
  firestore,
  functions,
  getDocs,
  httpsCallable,
  limit,
  query,
  where,
} from "@/firebase/init";

/**
 * Loads a tenant integration secret via Cloud Function (GSM via Secret Manager,
 * with Firestore legacy fallback on the server until columns are stripped).
 *
 * @param {string} fieldKey e.g. "shipstation:apiKey" or "leaflink:apiKey"
 * @param {{ payableToId?: string }} [options] Required for payableTo-scoped keys.
 * @returns {Promise<string>}
 */
export async function fetchIntegrationCredential(fieldKey, options = {}) {
  const { payableToId } = options;
  const getCredential = httpsCallable(functions, "getIntegrationCredential");
  const payload = { fieldKey };
  if (payableToId) {
    payload.payableToId = payableToId;
  }
  const result = await getCredential(payload);
  const data = result?.data;

  if (
    !data?.ok ||
    typeof data.credential !== "string" ||
    !data.credential.trim()
  ) {
    throw new Error(
      "Could not load integration credential. Check Admin → Integrations.",
    );
  }

  return data.credential.trim();
}

/**
 * Builds a vendor client when the integrations row already exists.
 *
 * @template T
 * @param {new (...args: any[]) => T} ClientClass
 * @param {string} fieldKey e.g. "method:apiKey"
 * @param {object} [clientOptions]
 * @returns {Promise<T>}
 */
export async function createIntegrationClient(
  ClientClass,
  fieldKey,
  clientOptions = {},
) {
  const apiKey = await fetchIntegrationCredential(fieldKey);
  return new ClientClass({ apiKey, ...clientOptions });
}

/**
 * Loads integrations/{platform} (if present) and returns a configured client.
 *
 * @template T
 * @param {string} platform
 * @param {new (...args: any[]) => T} ClientClass
 * @param {{ fieldKey?: string, clientOptions?: object }} [options]
 * @returns {Promise<T|null>}
 */
export async function initTenantIntegrationClient(
  platform,
  ClientClass,
  options = {},
) {
  const { fieldKey = `${platform}:apiKey`, clientOptions = {} } = options;
  try {
    const integrationsQuery = query(
      collection(firestore, "integrations"),
      where("platform", "==", platform),
      limit(1),
    );
    const snap = await getDocs(integrationsQuery);
    if (snap.empty) {
      return null;
    }
    return await createIntegrationClient(ClientClass, fieldKey, clientOptions);
  } catch (err) {
    console.error(`initTenantIntegrationClient(${platform})`, err);
    return null;
  }
}
